// src/encounter/care-plan-tab/encounter-care-plan-tab.tsx
// The encounter's "Care plan" tab (`?x=care-plan-tab`): reads the patient's care plans (C5, with B1 activities), the
// current steps (C4) and each plan's care team (A2) through TanStack Query hooks, and the signed-in user from the auth
// store, and hands CarePlanTabView the state. Loading, a failed read and "no care plan" stay apart. Review (accept) and
// Sign go through B1's activity actions; the server decides who may (403 in words).
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/auth/auth-store';
import { isDoctor } from '@/auth/roles';
import { useCarePlans, patientEnrolmentsKey } from '../services/use-care-plans';
import { fetchPatientEnrolments } from '../services/care-model-api';
import { withCurrentSteps, type CarePlanView } from '../lib/care-plan-view';
import type { CarePlanViewer } from './care-plan-board';
import { CarePlanTabView, type CarePlanTabViewProps } from './care-plan-view';
import { activityActionError, useActivityAction } from './use-activity-action';
import { useCarePlanTeams } from './use-care-plan-teams';

const NO_PLANS: CarePlanView[] = [];

export function EncounterCarePlanTab({ patientId }: { patientId: string }) {
  const plans = useCarePlans(patientId);
  const enrolments = useQuery({
    queryKey: patientEnrolmentsKey(patientId),
    enabled: (plans.data?.length ?? 0) > 0,
    queryFn: ({ signal }) => fetchPatientEnrolments(patientId, signal),
  });
  const user = useAuthStore((s) => s.user);
  const fullName = [user?.firstName, user?.lastName].map((s) => s?.trim()).filter(Boolean).join(' ');
  const viewer: CarePlanViewer | null = user ? { userId: user.userId, name: fullName || null, isPhysician: isDoctor(user) } : null;
  const action = useActivityAction(patientId);
  const [actionError, setActionError] = useState<{ carePlanId: string; message: string } | null>(null);
  const views = plans.data && plans.data.length > 0 ? withCurrentSteps(plans.data, enrolments.isSuccess ? enrolments.data : null) : NO_PLANS;
  const teams = useCarePlanTeams(patientId, views, viewer);
  const state: CarePlanTabViewProps['state'] = plans.isError
    ? { kind: 'error' }
    : !plans.data
      ? { kind: 'loading' }
      : plans.data.length === 0
        ? { kind: 'empty' }
        : { kind: 'plans', plans: views };
  return (
    <CarePlanTabView
      state={state}
      teams={teams}
      viewer={viewer}
      busy={action.isPending}
      actionError={actionError}
      onAction={(carePlanId, activity, kind) => {
        setActionError(null);
        action.mutate(
          { carePlanId, activityId: activity.id, action: kind, revision: activity.revision },
          { onError: (e) => setActionError({ carePlanId, message: activityActionError(e) }) },
        );
      }}
    />
  );
}
